import { toast } from "sonner";
import { getInvoicePdfBlob } from "./api";

/**
 * Re-send an issued invoice to the customer.
 *
 * Strategy:
 *  1. Fetch the invoice PDF from the existing endpoint.
 *  2. If the browser supports sharing files (iOS Safari ≥15, Android Chrome,
 *     desktop Edge etc.) → open the native share sheet with the PDF attached.
 *  3. Otherwise → save the PDF locally and open WhatsApp pre-filled with the
 *     message; the user attaches the just-downloaded file manually.
 *
 * The message starts with `[MN-XXX] ` (when a customer number is provided)
 * so the WhatsApp thread stays linkable to a customer record at a glance.
 *
 * @param {object} quote - the Quote document
 * @param {object} settings - app settings
 * @param {string} [customerNumberLabel] - optional MN-XXX tag to prepend
 */
export const shareInvoicePdf = async (quote, settings = {}, customerNumberLabel = "") => {
  if (!quote?.id || !quote.invoice_number) {
    toast.error("This quote has no invoice yet");
    return { method: "noop" };
  }

  const businessName = settings.business_name || "";
  const firstName = (quote.client_name || "").split(" ")[0] || "";
  const tag = customerNumberLabel ? `[${customerNumberLabel}] ` : "";
  const greeting = firstName
    ? `${tag}Hi ${firstName}, here's your invoice ${quote.invoice_number}`
    : `${tag}Here's your invoice ${quote.invoice_number}`;
  const sigBits = [businessName, settings.business_phone].filter(Boolean);
  const sig = sigBits.length ? `\n\n— ${sigBits.join(" · ")}` : "";
  const message = `${greeting} from ${businessName || "us"}.${sig}`;

  let blob;
  try {
    blob = await getInvoicePdfBlob(quote.id);
  } catch {
    toast.error("Couldn't fetch the invoice PDF");
    return { method: "error" };
  }

  const safe = (quote.client_name || "invoice").trim().replace(/\s+/g, "_");
  const filename = `${quote.invoice_number}_${safe}.pdf`;
  const file = new File([blob], filename, { type: "application/pdf" });

  // Native share sheet — best path on iOS / Android.
  if (
    typeof navigator !== "undefined" &&
    navigator.canShare &&
    navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({
        files: [file],
        title: `Invoice ${quote.invoice_number}`,
        text: message,
      });
      return { method: "native" };
    } catch (err) {
      if (err && err.name === "AbortError") {
        return { method: "cancelled" };
      }
      // fall through to fallback
    }
  }

  // Fallback: download + open WhatsApp pre-filled with the message.
  // If we have the customer's phone, target their thread directly.
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);

  const phoneDigits = (quote.client_phone || "").replace(/[^0-9]/g, "");
  const waUrl = phoneDigits
    ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;
  window.open(waUrl, "_blank", "noopener,noreferrer");
  toast.success("Invoice downloaded — attach it in WhatsApp");
  return { method: "fallback" };
};


const fmtMoney = (n) => `€${(Number(n) || 0).toFixed(2)}`;

/**
 * Build a friendly balance-reminder WhatsApp message for a customer with
 * outstanding money. Includes the bank transfer details from settings so
 * they don't have to look them up.
 *
 * @param {object} customer - { name, balance, deposits_paid_sum, status }
 * @param {object} settings - app settings (business_name, business_phone, bank_*)
 */
export const buildBalanceReminderText = (customer, settings = {}) => {
  const firstName = (customer?.name || "").split(" ")[0] || "";
  const balance = Number(customer?.balance || 0);
  const deposits = Number(customer?.deposits_paid_sum || 0);
  const businessName = settings.business_name || "";
  const businessPhone = settings.business_phone || "";
  const tag = customer?.customer_number_label
    ? `[${customer.customer_number_label}] `
    : "";

  const lines = [];
  lines.push(
    firstName
      ? `${tag}Hi ${firstName}, just a friendly reminder —`
      : `${tag}Hi, just a friendly reminder —`
  );
  if (deposits > 0 && customer?.status === "deposit") {
    lines.push(
      `${fmtMoney(balance)} balance is outstanding on your mosquito screens (deposit of ${fmtMoney(
        deposits
      )} received, thank you).`
    );
  } else {
    lines.push(`${fmtMoney(balance)} is outstanding on your mosquito screens.`);
  }

  const bankBits = [];
  if (settings.bank_account_holder) bankBits.push(settings.bank_account_holder);
  if (settings.bank_name) bankBits.push(settings.bank_name);
  if (settings.bank_iban) bankBits.push(`IBAN  ${settings.bank_iban}`);
  if (settings.bank_bic) bankBits.push(`BIC   ${settings.bank_bic}`);

  if (bankBits.length) {
    lines.push("");
    lines.push("*Bank transfer*");
    lines.push(...bankBits);
  }

  lines.push("");
  lines.push("Thanks!");
  const sig = [businessName, businessPhone].filter(Boolean).join(" · ");
  if (sig) lines.push(`— ${sig}`);

  return lines.join("\n");
};

/**
 * Open WhatsApp pre-filled with a polite balance reminder.
 * If the customer has a phone number, opens their WhatsApp thread directly;
 * otherwise the OS contact picker.
 */
export const sendBalanceReminder = (customer, settings = {}) => {
  if (!customer || !(customer.balance > 0)) {
    toast.error("This customer has no outstanding balance");
    return;
  }
  const text = buildBalanceReminderText(customer, settings);
  const phoneDigits = (customer.phone || "").replace(/[^0-9]/g, "");
  const url = phoneDigits
    ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(text)}`
    : `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener,noreferrer");
};
